import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useBreadcrumbs } from "@/contexts/BreadcrumbContext";
import { logger } from "@/utils/logger";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Gauge,
  ListChecks,
  Info,
  Activity,
  Loader2,
  Calendar,
} from "lucide-react";
import { SimpleTable } from "@/components/SimpleTable";
import { ColumnDef, PaginationState, Updater } from "@tanstack/react-table";
import { ModalAnalysis } from "@/components/modal-analysis";

// Define analysis history item type
interface AnalysisHistoryItem {
  timestamp: string;
  status: string;
  details?: string;
}

// Helper to get full avatar URL
const getAvatarUrl = (relativeUrl: string | undefined) => {
  if (!relativeUrl) return undefined;
  if (relativeUrl.match(/^https?:\/\//)) {
    return relativeUrl;
  }
  const apiUrl = (
    import.meta.env.VITE_API_URL || "http://localhost:8000"
  ).replace(/(\/api)?\/?$/, "");
  const cleanRelativeUrl = relativeUrl.startsWith("/")
    ? relativeUrl.substring(1)
    : relativeUrl;
  return `${apiUrl}/${cleanRelativeUrl}`;
};

// Interface for user info (matches backend UserInfoSchema)
interface UserInfo {
  username: string;
  name?: string | null;
  avatar_url?: string | null;
}

// Interface for persistent notes (matches backend PersistentNoteSchema)
interface PersistentNote {
  text: string;
  user: UserInfo;
  timestamp: string; // ISO string
}

// Interface for assignment history (matches backend ReleaseNoteAssignmentHistorySchema)
interface AssignmentHistory {
  action: string;
  user: UserInfo;
  timestamp: string; // ISO string
  assigned_user?: UserInfo | null;
}

interface DashboardSchema {
  _id: string;
  title: string;
  url?: string | null;
  description?: string | null;
  categories: string[];
  body_html?: string | null;

  persistent_status?: string | null;
  status_updated_by?: UserInfo | null;
  status_updated_at?: string | null; // ISO string
  persistent_notes: PersistentNote[];

  last_seen_in_run_id?: string | null;
  last_analysis_update_needed?: boolean | null;
  last_analysis_timestamp?: string | null; // ISO string
  analysis_history?: AnalysisHistoryItem[]; // Define analysis history item type
  db_last_updated?: string | null; // ISO string

  assigned_to?: UserInfo | null;
  assignment_history: AssignmentHistory[];
}

interface DashboardProps {
  apiBaseUrl: string;
}

const Dashboard: React.FC<DashboardProps> = ({ apiBaseUrl }) => {
  const [dashboardData, setDashboardData] = useState<DashboardSchema[] | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const { setBreadcrumbs } = useBreadcrumbs();

  // Add pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Add modal state
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);

  useEffect(() => {
    setBreadcrumbs([{ label: "Dashboard" }]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Expect an array of DashboardSchema (articles)
        const response = await axios.get<DashboardSchema[]>(
          `${apiBaseUrl}/canonical/dashboard`,
          { withCredentials: true }, // Ensure cookies (auth token) are sent
        );
        setDashboardData(response.data);
        logger.info("Successfully fetched dashboard data:", response.data);
      } catch (err) {
        logger.error("Error fetching dashboard data:", err);
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          setError("Authentication failed. Please log in again.");
          // Optionally redirect to login page
        } else {
          setError("Failed to load dashboard data.");
        }
        setDashboardData(null); // Clear data on error
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [apiBaseUrl]);

  const filteredArticles = useMemo(() => {
    if (!dashboardData) return [];

    return dashboardData.filter((article) => {
      const matchesSearch =
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || article.persistent_status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [dashboardData, searchQuery, statusFilter]);

  // Add pagination handler
  const handlePaginationChange = (updater: Updater<PaginationState>) => {
    const newPaginationState =
      typeof updater === "function"
        ? updater({ pageIndex: page - 1, pageSize })
        : updater;

    if (newPaginationState.pageSize !== pageSize) {
      setPageSize(newPaginationState.pageSize);
      setPage(1);
    } else if (newPaginationState.pageIndex + 1 !== page) {
      setPage(newPaginationState.pageIndex + 1);
    }
  };

  // Calculate total pages
  const totalPages = useMemo(() => {
    if (!filteredArticles) return 0;
    return Math.ceil(filteredArticles.length / pageSize);
  }, [filteredArticles, pageSize]);

  const getStatusColor = (status: string | null) => {
    switch (status?.toLowerCase()) {
      case "open":
        return "bg-primary text-primary-content";
      case "needs review":
        return "bg-warning/10 text-warning";
      case "in progress":
        return "bg-warning/10 text-warning";
      case "completed":
        return "bg-success/10 text-success";
      case "n/a":
        return "bg-success/10 text-success";
      default:
        return "bg-base-content/5 text-base-content/70";
    }
  };

  const columns: ColumnDef<DashboardSchema>[] = [
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => (
        <Link
          to={`/articles/${row.original._id}`}
          className="flex items-center gap-3 text-base-content transition-colors"
        >
          <div className="font-medium">{row.getValue("title")}</div>
        </Link>
      ),
    },
    {
      accessorKey: "assigned_to",
      header: "Assigned To",
      cell: ({ row }) => {
        const assignedTo = row.original.assigned_to;
        if (!assignedTo) return <span className="text-base-content/50">-</span>;

        return (
          <div className="flex items-center gap-2 select-none">
            {assignedTo.avatar_url ? (
              <img
                className="size-6 rounded-full ring-2 ring-base-300/50"
                src={getAvatarUrl(assignedTo.avatar_url)}
                alt={assignedTo.name || assignedTo.username}
              />
            ) : (
              <div className="flex size-6 items-center justify-center rounded-full bg-primary/10 ring-2 ring-base-300/50">
                <Info className="size-4 text-primary" />
              </div>
            )}
            <span>{assignedTo.name || assignedTo.username}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "persistent_status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.getValue("persistent_status") as string | null;
        return (
          <div
            className={`badge font-mono text-xs shadow-md select-none ${getStatusColor(status)}`}
          >
            {status || "No Status"}
          </div>
        );
      },
    },
    {
      accessorKey: "status_updated_at",
      header: "Last Updated",
      cell: ({ row }) => {
        const date = row.getValue("status_updated_at") as string;
        return date ? (
          <div className="flex items-center gap-2 text-base-content/70">
            <Calendar className="size-4" />
            <span>{new Date(date).toLocaleDateString("sv-SE")}</span>
          </div>
        ) : (
          <span className="text-base-content/50">No updates</span>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <span className="text-lg text-base-content/70">
            Loading Dashboard...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto mt-8 max-w-2xl">
        <div className="rounded-2xl border border-error/20 bg-error/5 p-6 text-error backdrop-blur-lg">
          <div className="mb-2 flex items-center gap-3">
            <AlertTriangle className="h-6 w-6" />
            <span className="text-lg font-semibold">
              Error Loading Dashboard
            </span>
          </div>
          <p className="text-error/80">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-screen-2xl space-y-8 p-8">
      {/* Header Section */}
      <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
        <div>
          <h1 className="bg-gradient-to-r from-base-content/80 to-base-content bg-clip-text text-3xl font-bold text-transparent">
            Dashboard
          </h1>
          <p className="text-md mt-2 text-base-content/70">
            Manage your assigned articles and track their progress
          </p>
        </div>
        <div className="flex w-full items-center gap-3 md:w-auto">
          {/* Search Input */}
          <input
            type="text"
            placeholder="Search articles..."
            className="input-bordered input input-sm w-full max-w-xs"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select
            className="rounded-xl border border-base-300/50 bg-base-200/30 px-4 py-2 text-sm text-base-content backdrop-blur-xl focus:ring-2 focus:ring-primary/20 focus:outline-none"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="Open">Open</option>
            <option value="Needs Review">Needs Review</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
            <option value="N/A">N/A</option>
          </select>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <div className="rounded-2xl border border-base-300/50 bg-base-200/30 p-6 backdrop-blur-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-base-content/70">
                Total Articles
              </p>
              <p className="mt-1 text-2xl font-bold text-base-content">
                {dashboardData?.length || 0}
              </p>
            </div>
            <div className="rounded-xl bg-primary/10 p-3">
              <ListChecks className="h-6 w-6 text-primary" />
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-base-300/50 bg-base-200/30 p-6 backdrop-blur-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-base-content/70">
                Ongoing
              </p>
              <p className="mt-1 text-2xl font-bold text-base-content">
                {dashboardData?.filter(
                  (a) =>
                    a.persistent_status === "In Progress" ||
                    a.persistent_status === "Needs Review" ||
                    a.persistent_status === "Open",
                ).length || 0}
              </p>
            </div>
            <div className="rounded-xl bg-warning/10 p-3">
              <Activity className="h-6 w-6 text-warning" />
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-base-300/50 bg-base-200/30 p-6 backdrop-blur-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-base-content/70">
                Completed
              </p>
              <p className="mt-1 text-2xl font-bold text-base-content">
                {dashboardData?.filter(
                  (a) =>
                    a.persistent_status === "Completed" ||
                    a.persistent_status === "N/A",
                ).length || 0}
              </p>
            </div>
            <div className="rounded-xl bg-success/10 p-3">
              <Gauge className="h-6 w-6 text-success" />
            </div>
          </div>
        </div>
      </div>

      {/* Articles Table */}
      <div className="overflow-hidden border-transparent bg-base-200/30 backdrop-blur-xl">
        {filteredArticles.length > 0 ? (
          <SimpleTable
            columns={columns}
            data={filteredArticles}
            isLoading={loading}
            skeletonRowCount={pageSize}
            pageCount={totalPages}
            pagination={{ pageIndex: page - 1, pageSize }}
            onPaginationChange={handlePaginationChange}
          />
        ) : (
          <div className="py-16">
            <div className="flex flex-col items-center gap-4">
              <div className="rounded-full bg-base-300/30 p-4">
                <ListChecks className="h-8 w-8 text-base-content/50" />
              </div>
              <div className="text-center">
                <p className="text-xl font-medium text-base-content">
                  No articles found
                </p>
                <p className="mt-1 text-base-content/70">
                  {dashboardData?.length === 0
                    ? "You currently have no articles assigned to you."
                    : "No articles match your current filters."}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Analysis Modal */}
      <ModalAnalysis
        modalId="analysis-modal"
        isOpen={isAnalysisModalOpen}
        onClose={() => setIsAnalysisModalOpen(false)}
        apiBaseUrl={apiBaseUrl}
      />
    </div>
  );
};

export default Dashboard;
